import { Skeleton, Badge } from '@radix-ui/themes';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { getSurfaceType, italianDateToJSDate } from 'helpers/utils';
import type { ClubDetailType } from 'types/club';
import type { CompetitionClubType } from 'types/competition';
import type { TournamentType } from 'types/tournament';

export const CompetitionObject: React.FC<
  Partial<CompetitionClubType> & {
    loading?: boolean;
    dates?: string;
    tournaments?: Partial<TournamentType>[];
    club?: Partial<ClubDetailType>;
  }
> = ({ loading, to, from, name, id, dates, club, tournaments }) => {
  const itemClassName = 'p-3 rounded-xl border border-solid border-neutral-neutral-2 hover:bg-accent-accent-3';
  const labelClassName = 'self-stretch text-neutral-neutral-8 text-sm font-medium tracking-normal';
  const contentClassName =
    'flex justify-between gap-2 text-wrap self-stretch text-neutral-neutral-alpha-12 text-base font-bold tracking-tighter';

  const tournamentClassName =
    'bg-neutral-neutral-1 p-3 rounded-xl border border-solid border-neutral-neutral-2 flex flex-row justify-between grow text-neutral-neutral-12 text-xl font-bold tracking-tight';
  const date = new Date();
  const liveBadge =
    date <= italianDateToJSDate(to) && date >= italianDateToJSDate(from) ? (
      <Badge size='1' variant='solid' color='indigo'>
        IN CORSO
      </Badge>
    ) : date < italianDateToJSDate(from) ? (
      <Badge size='1' variant='solid' color='pink'>
        PROGRAMMATA
      </Badge>
    ) : (
      <Badge size='1' variant='solid' color='amber'>
        CONCLUSA
      </Badge>
    );

  return (
    <Link className='w-full' to={`/competizione/${id}`}>
      <div id='competition-scheduled' className={classNames('bg-white', itemClassName)}>
        <Skeleton loading={loading} className='h-auto w-auto'>
          <div id='name' className={contentClassName}>
            {name}
            <div className='flex flex-col items-end gap-2'>
              {to && liveBadge}

              {/* {<Badge size='1' variant='outline'>
                  {rankCategory}
                </Badge>} */}
            </div>
          </div>
        </Skeleton>
        {to && (
          <div id='info' className='flex w-full justify-between pt-2'>
            <Skeleton loading={loading} className='h-auto w-auto'>
              <div id='date' className={labelClassName}>
                {from} - {to}
              </div>
            </Skeleton>
            {/* <Skeleton loading={loading} className='w-auto h-auto'>
            <div id='players' className={labelClassName}>
              {} iscritti
            </div>
          </Skeleton> */}
          </div>
        )}
        {dates && (
          <div id='info' className='flex w-full justify-between pt-2'>
            <Skeleton loading={loading} className='h-auto w-auto'>
              <div id='date' className={labelClassName}>
                {dates}
              </div>
            </Skeleton>
            {club && (
              <Skeleton loading={loading} className='h-auto w-auto'>
                <div id='players' className={labelClassName}>
                  {club.name}
                </div>
              </Skeleton>
            )}
          </div>
        )}
        {tournaments && (
          <Skeleton loading={loading} className='h-auto w-auto'>
            <div className={classNames(labelClassName, 'flex flex-col gap-2 pt-2')}>
              {tournaments.map((tournament) => (
                <div key={tournament.id} className={tournamentClassName}>
                  <div className='flex flex-col gap-3 text-sm font-medium'>
                    <div>{tournament.name}</div>
                    {tournament.surface && (
                      <div className='flex flex-row items-center gap-1'>
                        Superficie:
                        <Badge
                          size='1'
                          variant='solid'
                          className={classNames('flex items-center justify-center text-white', {
                            'bg-accent-accent-9':
                              tournament.surface === 'grass_court' || tournament.surface === 'synthetic_grass',
                            'bg-red-8': tournament.surface === 'clay_court' || tournament.surface === 'synthetic_clay',
                            'bg-blue-9': tournament.surface === 'hard_court' || tournament.surface === 'synthetic',
                            'bg-neutral-neutral-8': tournament.surface === 'other',
                          })}
                        >
                          {getSurfaceType(tournament.surface)}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <div id='separator' className='bg-neutral-neutral-4 mx-1 flex h-10 w-px self-stretch md:h-4' />
                  <div className='flex flex-col gap-3 text-sm font-bold md:flex-row'>
                    <div className='flex flex-row items-center gap-1'>
                      Iscritti
                      <Badge
                        size='1'
                        variant='solid'
                        className={classNames(
                          'flex items-center justify-center text-white',
                          {
                            'bg-accent-accent-9':
                              (tournament.players_count || 1) < (tournament.max_number_partecipants || 500),
                          },
                          {
                            'bg-accent-accent-8': tournament.players_count === tournament.max_number_partecipants,
                          },
                          {
                            'bg-red-8': (tournament.players_count || 1) > (tournament.max_number_partecipants || 500),
                          },
                        )}
                      >
                        {tournament.players_count}
                        {tournament.max_number_partecipants ? ` / ${tournament.max_number_partecipants}` : ''}
                      </Badge>
                    </div>
                    {tournament.sign_up_order && (
                      <div className='flex items-center font-normal'>
                        {tournament.sign_up_order === 'ranking' ? 'Ordine di classifica' : 'Ordine di iscrizione'}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Skeleton>
        )}
      </div>
    </Link>
  );
};

export default CompetitionObject;
